import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import './Letterhead.css';
import 'react-quill-new/dist/quill.snow.css'; // Import Quill styles
import logo from '../../Assets/nav-logo.png';
import TaxPortalLayout from '../../components/Common/TaxPortalLayout';
import { useTaxPortal } from '../../utils/useTaxPortal';

// Defined outside the component so Quill doesn't re-initialise on every render
const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['clean'],
    ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'align'];

// Footer artwork (curve, triangles AND contact text) as ONE image.
// An <img> prints reliably on every page, exactly like the logo does.
const FOOTER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 902 308">
<path d="M245 170 Q376 65 902 0 L902 308 L200 308 Z" fill="#f0ece4"/>
<polygon points="0,115 245,170 200,308" fill="#ddd3bd"/>
<polygon points="245,170 395,280 395,308 200,308" fill="#c5b794"/>
<g font-family="'Times New Roman', Times, serif" font-size="17.1" fill="#2b2418">
<text x="98" y="212">2970 Drew Road, Unit 201 , Mississauga L4T0A6</text>
<text x="98" y="238">Cell : (647) 855-6177<tspan dx="9" text-decoration="underline">info@orientalbiz.ca</tspan><tspan dx="9" text-decoration="underline">www.orientalbiz.ca</tspan></text>
<text x="98" y="264">HST No :710351909 RT0001</text>
</g>
</svg>`;
const FOOTER_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(FOOTER_SVG)}`;

const OrientalbizLetterhead = () => {
    const portalState = useTaxPortal();
    const [editorHtml, setEditorHtml] = useState('');
    const pageRef = useRef(null);
    const bodyRef = useRef(null);

    // Add and remove the body class for scoped print styles
    useEffect(() => {
        document.body.classList.add('letterhead-body');
        return () => {
            document.body.classList.remove('letterhead-body');
        };
    }, []);

    // Before printing, work out whether the letter fits on ONE page.
    // One page: logo, corner, curve and footer are placed on the page itself.
    // Several pages: they are fixed so they repeat on every page.
    useEffect(() => {
        const PX_PER_IN = 96;
        const beforePrint = () => {
            const page = pageRef.current;
            const body = bodyRef.current;
            if (!page || !body) return;
            const toolbar = page.querySelector('.ql-toolbar');
            const toolbarH = toolbar ? toolbar.offsetHeight + 12 : 0;
            const contentH = body.scrollHeight - toolbarH;
            const totalIn = contentH / PX_PER_IN + 1.85 + 1.6; // + header and footer space
            page.classList.toggle('is-single', totalIn <= 10.4);
        };
        const afterPrint = () => {
            if (pageRef.current) pageRef.current.classList.remove('is-single');
        };
        window.addEventListener('beforeprint', beforePrint);
        window.addEventListener('afterprint', afterPrint);
        return () => {
            window.removeEventListener('beforeprint', beforePrint);
            window.removeEventListener('afterprint', afterPrint);
        };
    }, []);

    const handlePrint = () => {
        window.print();
    };

    const today = new Date().toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <TaxPortalLayout
            title="Company Letterhead"
            description="Create and print official company correspondence."
            portalState={portalState}
            isFluid={true}
        >
            <div className="letterhead-wrapper">
                <div className="document-page" ref={pageRef}>

                    {/* DECORATION: red corner, top-left (repeats on every printed page) */}
                    <svg className="decor decor-top" viewBox="0 0 190 120" aria-hidden="true">
                        <polygon points="0,0 165,0 0,100" fill="#e11d48" opacity="0.9" />
                        <polygon points="45,0 190,0 20,105 0,100 0,60" fill="#fda4af" opacity="0.55" />
                    </svg>

                    {/* FOOTER: curve, triangles and contact text as one image
                        (repeats on every printed page) */}
                    <div className="lh-bottom">
                        <img src={FOOTER_SRC} alt="2970 Drew Road, Unit 201, Mississauga L4T0A6. Cell (647) 855-6177. info@orientalbiz.ca. www.orientalbiz.ca. HST No 710351909 RT0001" />
                    </div>

                    {/* HEADER: centered logo (repeats on every printed page) */}
                    <header className="brand-header">
                        <div className="logo-container">
                            <img
                                src={logo}
                                alt="Oriental Business Solutions Inc. Logo"
                                className="company-logo"
                            />
                        </div>
                    </header>

                    {/* CONTENT FRAME: the empty thead/tfoot rows repeat in print
                        so text never runs under the logo or contact block */}
                    <table className="print-frame">
                        <thead>
                            <tr><td><div className="head-space" /></td></tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td ref={bodyRef}>
                                    <div className="document-meta">
                                        <p><strong>Date:</strong> {today}</p>
                                    </div>

                                    <main className="document-body">
                                        <ReactQuill
                                            theme="snow"
                                            value={editorHtml}
                                            onChange={setEditorHtml}
                                            modules={QUILL_MODULES}
                                            formats={QUILL_FORMATS}
                                            placeholder="Type official correspondence here..."
                                            className="content-input-quill"
                                        />
                                    </main>

                                    <div className="signature-blocks">
                                        <div className="sig-line">
                                            <span className="line"></span>
                                            <span className="label">Authorized Signature</span>
                                        </div>
                                        <div className="sig-line">
                                            <span className="line"></span>
                                            <span className="label">Date</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr><td><div className="foot-space" /></td></tr>
                        </tfoot>
                    </table>
                </div>

                {/* Controls: hidden during printing */}
                <div className="no-print control-panel">
                    <button onClick={handlePrint} className="print-button">
                        Print to Official PDF
                    </button>
                    <p className="hint-text">
                        Set margins to "None" in your print dialog. Colours and shapes are preserved automatically.
                    </p>
                </div>
            </div>
        </TaxPortalLayout>
    );
};

export default OrientalbizLetterhead;